import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/site";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full bg-[#0A1A36] text-white transition-shadow ${
        scrolled ? "shadow-[0_2px_0_0_#D4AF37]" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo-link">
            <img
              src={COMPANY.logoUrl}
              alt="Asset Group Pte Ltd"
              className="h-12 w-12 object-contain"
              data-testid="navbar-logo-image"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-extrabold text-lg tracking-tight">ASSET GROUP</span>
              <span className="text-[10px] tracking-[0.25em] text-[#D4AF37]">BUILD. GROW. PROSPER.</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `ag-link-underline text-sm tracking-wide font-medium uppercase ${
                    isActive ? "text-[#D4AF37]" : "text-white/90 hover:text-white"
                  }`
                }
                data-active={undefined}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="hidden xl:flex items-center gap-2 text-sm text-white/80 hover:text-[#D4AF37] ag-btn-hover"
              data-testid="navbar-phone-link"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phone}
            </a>
            <Link to="/quote" data-testid="navbar-quote-button">
              <Button className="rounded-none bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider ag-btn-hover">
                Request a Quote
              </Button>
            </Link>
          </div>

          <button
            className="lg:hidden p-2 -mr-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0A1A36]" data-testid="navbar-mobile-menu">
          <div className="px-4 sm:px-6 py-6 space-y-4">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block text-base font-medium uppercase tracking-wider ${
                    isActive ? "text-[#D4AF37]" : "text-white/90"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link to="/quote" data-testid="navbar-mobile-quote-button">
              <Button className="w-full mt-2 rounded-none bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider">
                Request a Quote
              </Button>
            </Link>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="flex items-center gap-2 text-sm text-white/80"
              data-testid="navbar-mobile-phone-link"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
